import React, { useState, useEffect, useRef, useCallback } from 'react';
import { propertiesApi } from '../services/api';
import Sidebar from '../components/Sidebar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Search,
  Home,
  MapPin,
  Euro,
  Edit,
  Trash2,
  Loader2,
  Camera,
  X,
  Image as ImageIcon,
  Users,
  UserPlus,
  Download,
  Key,
  Bell,
  AlertTriangle,
  Pencil,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PropertiesManagement = () => {
  const [properties, setProperties] = useState([]);
  const [housekeepers, setHousekeepers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postcode: '',
    property_type: '',  // Type de logement
    cleaning_price: '',
    custom_prices: [],
    notes: '',
    keybox_code: '',
    keybox_address: '',
    keybox_position: '',
    building_code: '',
    access_instructions: '',
    linen_included: false,
    instruction_photos: [],
    ring_device_id: '',
    ring_device_name: '',
    latitude: '',
    longitude: ''
  });
  
  // Types de logements disponibles
  const propertyTypes = [
    { value: 'studio', label: 'Studio' },
    { value: 't1', label: 'T1' },
    { value: 't2', label: 'T2' },
    { value: 't3', label: 'T3' },
    { value: 't4', label: 'T4+' },
    { value: 'maison', label: 'Maison' },
    { value: 'villa', label: 'Villa' }
  ];
  
  const [actionLoading, setActionLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [importingProperties, setImportingProperties] = useState(false);
  const [geocodingProperties, setGeocodingProperties] = useState(false);
  const fileInputRef = useRef(null);
  const [ringIntercoms, setRingIntercoms] = useState([]);
  
  // Address autocomplete
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const addressInputRef = useRef(null);
  
  // Verrouillage de l'adresse (pour éviter les modifications accidentelles)
  const [addressLocked, setAddressLocked] = useState(false);

  // Keybox address autocomplete
  const [keyboxAddressQuery, setKeyboxAddressQuery] = useState('');
  const [keyboxAddressSuggestions, setKeyboxAddressSuggestions] = useState([]);
  const [showKeyboxAddressSuggestions, setShowKeyboxAddressSuggestions] = useState(false);
  const [loadingKeyboxAddress, setLoadingKeyboxAddress] = useState(false);

  // Debounce pour la recherche d'adresse
  useEffect(() => {
    if (addressQuery.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoadingAddress(true);
      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(addressQuery)}&limit=5`
        );
        const data = await response.json();
        if (data.features) {
          setAddressSuggestions(data.features.map(f => ({
            label: f.properties.label,
            address: f.properties.name,
            city: f.properties.city,
            postcode: f.properties.postcode,
            context: f.properties.context,
            // Coordonnées GPS (longitude, latitude dans l'ordre GeoJSON)
            longitude: f.geometry?.coordinates?.[0] || null,
            latitude: f.geometry?.coordinates?.[1] || null
          })));
          setShowAddressSuggestions(true);
        }
      } catch (error) {
        console.error('Address API error:', error);
      } finally {
        setLoadingAddress(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [addressQuery]);

  // Debounce pour la recherche d'adresse boîte à clés
  useEffect(() => {
    if (keyboxAddressQuery.length < 3) {
      setKeyboxAddressSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoadingKeyboxAddress(true);
      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(keyboxAddressQuery)}&limit=5`
        );
        const data = await response.json();
        if (data.features) {
          setKeyboxAddressSuggestions(data.features.map(f => ({
            label: f.properties.label,
            address: f.properties.name,
            city: f.properties.city,
            postcode: f.properties.postcode,
            context: f.properties.context
          })));
          setShowKeyboxAddressSuggestions(true);
        }
      } catch (error) {
        console.error('Keybox address API error:', error);
      } finally {
        setLoadingKeyboxAddress(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [keyboxAddressQuery]);

  const selectAddress = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      address: suggestion.address,
      city: suggestion.city,
      postcode: suggestion.postcode,
      // Auto-remplir les coordonnées GPS
      latitude: suggestion.latitude ? suggestion.latitude.toString() : prev.latitude,
      longitude: suggestion.longitude ? suggestion.longitude.toString() : prev.longitude
    }));
    setAddressQuery(suggestion.label);
    setShowAddressSuggestions(false);
  };

  const selectKeyboxAddress = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      keybox_address: suggestion.label
    }));
    setKeyboxAddressQuery(suggestion.label);
    setShowKeyboxAddressSuggestions(false);
  };

  const fetchProperties = useCallback(async () => {
    try {
      const res = await propertiesApi.list();
      setProperties(res.data);
    } catch (error) {
      console.error('Properties error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHousekeepers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/users?assignable=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHousekeepers(data);
      }
    } catch (error) {
      console.error('Housekeepers error:', error);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
    fetchHousekeepers();
    fetchRingIntercoms();
  }, [fetchProperties, fetchHousekeepers]);

  const fetchRingIntercoms = async () => {
    try {
      const authToken = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ring/intercoms`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.configured && data.intercoms) {
          setRingIntercoms(data.intercoms);
        }
      }
    } catch (error) {
      console.error('Failed to fetch Ring intercoms:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.address) {
      toast.error('Remplissez les champs obligatoires');
      return;
    }
    setActionLoading(true);
    try {
      const data = {
        ...formData,
        cleaning_price: formData.cleaning_price ? parseFloat(formData.cleaning_price) : 0,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };
      if (editingProperty) {
        await propertiesApi.update(editingProperty.id, data);
        toast.success('Logement modifié');
      } else {
        await propertiesApi.create(data);
        toast.success('Logement créé');
      }
      setShowDialog(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce logement ?')) return;
    try {
      await propertiesApi.delete(id);
      toast.success('Logement supprimé');
      fetchProperties();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleImportProperties = async () => {
    if (!window.confirm('Importer les 116 logements de base de PlayMCB ?\n\nLes logements existants ne seront pas dupliqués.')) return;
    
    setImportingProperties(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/import-properties`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Import failed');
      
      const result = await response.json();
      toast.success(result.message);
      fetchProperties();
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    } finally {
      setImportingProperties(false);
    }
  };

  const handleGeocodeAll = async () => {
    if (!window.confirm('Géocoder automatiquement tous les logements sans coordonnées GPS ?\n\nCette opération peut prendre quelques secondes.')) return;
    
    setGeocodingProperties(true);
    try {
      const response = await fetch(`${API_URL}/api/properties/geocode-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Geocoding failed');
      
      const result = await response.json();
      
      if (result.success > 0) {
        toast.success(`${result.success} logement(s) géocodé(s) avec succès`);
        fetchProperties();
      }
      if (result.failed > 0) {
        toast.warning(`${result.failed} logement(s) n'ont pas pu être géocodés`);
      }
      if (result.total === 0) {
        toast.info('Tous les logements ont déjà des coordonnées GPS');
      }
    } catch (error) {
      toast.error('Erreur lors du géocodage');
    } finally {
      setGeocodingProperties(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', city: '', postcode: '', property_type: '', cleaning_price: '', custom_prices: [], notes: '', keybox_code: '', keybox_address: '', keybox_position: '', building_code: '', access_instructions: '', linen_included: false, instruction_photos: [], ring_device_id: '', ring_device_name: '', latitude: '', longitude: '' });
    setEditingProperty(null);
    setAddressQuery('');
    setAddressSuggestions([]);
    setKeyboxAddressQuery('');
    setKeyboxAddressSuggestions([]);
    // Déverrouiller l'adresse pour les nouveaux logements
    setAddressLocked(false);
  };

  const openEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      address: property.address,
      city: property.city || '',
      postcode: property.postcode || '',
      property_type: property.property_type || '',
      cleaning_price: property.cleaning_price?.toString() || '',
      custom_prices: property.custom_prices || [],
      notes: property.notes || '',
      keybox_code: property.keybox_code || '',
      keybox_address: property.keybox_address || '',
      keybox_position: property.keybox_position || '',
      building_code: property.building_code || '',
      access_instructions: property.access_instructions || '',
      linen_included: property.linen_included || false,
      instruction_photos: property.instruction_photos || [],
      ring_device_id: property.ring_device_id || '',
      ring_device_name: property.ring_device_name || '',
      latitude: property.latitude?.toString() || '',
      longitude: property.longitude?.toString() || ''
    });
    // Set address query to current full address for editing
    setAddressQuery(`${property.address || ''}, ${property.postcode || ''} ${property.city || ''}`.trim());
    setKeyboxAddressQuery(property.keybox_address || '');
    // Verrouiller l'adresse par défaut en mode édition
    setAddressLocked(true);
    setShowDialog(true);
  };

  // Custom prices management
  const addCustomPrice = (userId) => {
    if (formData.custom_prices.find(cp => cp.user_id === userId)) {
      toast.error('Cette personne a déjà un tarif personnalisé');
      return;
    }
    setFormData(prev => ({
      ...prev,
      custom_prices: [...prev.custom_prices, { user_id: userId, price: prev.cleaning_price || 0 }]
    }));
  };

  const updateCustomPrice = (userId, price) => {
    setFormData(prev => ({
      ...prev,
      custom_prices: prev.custom_prices.map(cp => 
        cp.user_id === userId ? { ...cp, price: parseFloat(price) || 0 } : cp
      )
    }));
  };

  const removeCustomPrice = (userId) => {
    setFormData(prev => ({
      ...prev,
      custom_prices: prev.custom_prices.filter(cp => cp.user_id !== userId)
    }));
  };

  const getHousekeeperName = (userId) => {
    const hk = housekeepers.find(h => h.id === userId);
    return hk?.name || 'Inconnu';
  };

  // Upload instruction photos
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhotos(true);
    const token = localStorage.getItem('token');
    const newPhotos = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await fetch(`${API_URL}/api/properties/upload-instruction-photo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataUpload
        });

        if (response.ok) {
          const data = await response.json();
          newPhotos.push(data.photo_url);
        } else {
          toast.error(`Erreur upload: ${file.name}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Erreur upload: ${file.name}`);
      }
    }

    if (newPhotos.length > 0) {
      setFormData(prev => ({
        ...prev,
        instruction_photos: [...prev.instruction_photos, ...newPhotos]
      }));
      toast.success(`${newPhotos.length} photo(s) ajoutée(s)`);
    }

    setUploadingPhotos(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      instruction_photos: prev.instruction_photos.filter((_, i) => i !== index)
    }));
  };

  // Filter properties
  const filteredProperties = properties.filter(p => {
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase()) && !p.address?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const PropertyTable = ({ data }) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead></TableHead>
          <TableHead>Nom</TableHead>
          <TableHead>Adresse</TableHead>
          <TableHead>Ville</TableHead>
          <TableHead>GPS</TableHead>
          <TableHead>Prix ménage</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-8 text-gray-500">
              Aucun logement trouvé
            </TableCell>
          </TableRow>
        ) : (
          data.map((property) => {
            const hasGPS = property.latitude && property.longitude;
            return (
            <TableRow key={property.id} data-testid={`property-row-${property.id}`} className={!hasGPS ? 'bg-red-50/50' : ''}>
              <TableCell className="w-4">
                <div 
                  className="w-4 h-4 rounded-full shadow-sm" 
                  style={{ backgroundColor: property.color || '#gray' }}
                  title={`Couleur: ${property.color}`}
                />
              </TableCell>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <Home className="h-4 w-4" style={{ color: property.color || '#9CA3AF' }} />
                  <span className={property.name === 'Inconnu' ? 'text-orange-600' : ''}>
                    {property.name}
                  </span>
                  {property.name === 'Inconnu' && (
                    <Badge className="bg-orange-100 text-orange-700 border-0">À identifier</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1 text-gray-600">
                  <MapPin className="h-3 w-3" />
                  {property.address || '-'}
                </div>
              </TableCell>
              <TableCell>{property.city || '-'}</TableCell>
              <TableCell>
                {hasGPS ? (
                  <Badge className="bg-green-100 text-green-700 border-0">
                    <MapPin className="h-3 w-3 mr-1" />
                    OK
                  </Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-700 border-0">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Erreur GPS
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1 font-medium text-[#003366]">
                  <Euro className="h-3 w-3" />
                  {property.cleaning_price || 0}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon" onClick={() => openEdit(property)} data-testid={`edit-property-${property.id}`}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(property.id)} data-testid={`delete-property-${property.id}`}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </TableCell>
            </TableRow>
          )})
        )}
      </TableBody>
    </Table>
  );

  // Calculer les stats GPS
  const propertiesWithoutGPS = properties.filter(p => !p.latitude || !p.longitude);
  const propertiesWithGPS = properties.filter(p => p.latitude && p.longitude);

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 overflow-auto ml-0 md:ml-72">
        <div className="p-4 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#003366]">Logements</h1>
              <div className="flex items-center gap-3 mt-1">
                <p className="text-sm text-gray-500">
                  {properties.length} logement{properties.length > 1 ? 's' : ''}
                </p>
                {propertiesWithoutGPS.length > 0 && (
                  <Badge className="bg-red-100 text-red-700 border-0">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {propertiesWithoutGPS.length} sans GPS
                  </Badge>
                )}
                {propertiesWithGPS.length > 0 && (
                  <Badge className="bg-green-100 text-green-700 border-0">
                    <MapPin className="h-3 w-3 mr-1" />
                    {propertiesWithGPS.length} OK
                  </Badge>
                )}
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
              <Button 
                onClick={handleImportProperties}
                variant="outline"
                className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                disabled={importingProperties}
                data-testid="import-properties-btn"
              >
                {importingProperties ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Importer logements MCB
              </Button>
              <Button 
                onClick={handleGeocodeAll}
                variant="outline"
                className={propertiesWithoutGPS.length > 0 ? "border-red-500 text-red-600 hover:bg-red-50" : "border-blue-500 text-blue-600 hover:bg-blue-50"}
                disabled={geocodingProperties}
                data-testid="geocode-properties-btn"
              >
                {geocodingProperties ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <MapPin className="h-4 w-4 mr-2" />
                )}
                Auto GPS {propertiesWithoutGPS.length > 0 && `(${propertiesWithoutGPS.length})`}
              </Button>
              <Button 
                onClick={() => { resetForm(); setShowDialog(true); }}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="add-property-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouveau logement
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom ou adresse..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-properties"
              />
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#003366]" />
            </div>
          ) : (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <PropertyTable data={filteredProperties} />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{editingProperty ? 'Modifier le logement' : 'Nouveau logement'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            {/* Nom et Type */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nom du logement *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Villa Bretonne"
                  data-testid="property-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <select
                  value={formData.property_type}
                  onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                  className="w-full h-10 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="property-type"
                >
                  <option value="">-- Type --</option>
                  {propertyTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Adresse avec autocomplétion et verrouillage */}
            <div className="space-y-2 relative">
              <Label className="flex items-center justify-between">
                <span>Adresse *</span>
                {editingProperty && addressLocked && (
                  <button
                    type="button"
                    onClick={() => setAddressLocked(false)}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    data-testid="unlock-address-btn"
                  >
                    <Pencil className="h-3 w-3" />
                    Modifier l'adresse
                  </button>
                )}
              </Label>
              
              {/* Mode verrouillé - Affichage lecture seule */}
              {addressLocked && editingProperty ? (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-800">{formData.address}</p>
                        <p className="text-sm text-slate-500">{formData.postcode} {formData.city}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Mode déverrouillé - Input avec autocomplétion */
                <>
                  <div className="relative">
                    <Input 
                      ref={addressInputRef}
                      value={addressQuery}
                      onChange={(e) => {
                        setAddressQuery(e.target.value);
                        // Clear form fields if typing new address
                        if (!e.target.value) {
                          setFormData(prev => ({ ...prev, address: '', city: '', postcode: '', latitude: '', longitude: '' }));
                        }
                      }}
                      onFocus={() => addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                      placeholder="Commencez à taper l'adresse..."
                      data-testid="property-address"
                      className="pr-8"
                    />
                    {loadingAddress && (
                      <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                    )}
                  </div>
                  
                  {/* Suggestions dropdown */}
                  {showAddressSuggestions && addressSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {addressSuggestions.map((suggestion) => (
                        <button
                          key={`${suggestion.address}-${suggestion.postcode}`}
                          type="button"
                          className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b last:border-0 transition-colors"
                          onClick={() => selectAddress(suggestion)}
                        >
                          <p className="font-medium text-gray-900 text-sm">{suggestion.address}</p>
                          <p className="text-xs text-gray-500">{suggestion.postcode} {suggestion.city}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Click outside to close */}
                  {showAddressSuggestions && (
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowAddressSuggestions(false)}
                    />
                  )}
                </>
              )}
            </div>

            {/* Affichage des champs remplis automatiquement (seulement si pas verrouillé) */}
            {!addressLocked && (formData.address || formData.city || formData.postcode) && (
              <div className="p-3 bg-blue-50 rounded-lg space-y-2 border border-blue-200">
                <p className="text-xs font-medium text-blue-800 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Adresse sélectionnée
                </p>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Adresse</p>
                    <p className="font-medium">{formData.address || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Code postal</p>
                    <p className="font-medium">{formData.postcode || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Ville</p>
                    <p className="font-medium">{formData.city || '-'}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code boîte à clés</Label>
                <Input 
                  value={formData.keybox_code}
                  onChange={(e) => setFormData({ ...formData, keybox_code: e.target.value })}
                  placeholder="1234"
                  data-testid="property-keybox"
                />
              </div>
              <div className="space-y-2">
                <Label>Code immeuble</Label>
                <Input 
                  value={formData.building_code}
                  onChange={(e) => setFormData({ ...formData, building_code: e.target.value })}
                  placeholder="A1234"
                  data-testid="property-building-code"
                />
              </div>
            </div>

            {/* GPS Coordinates for photo validation */}
            <div className="p-4 bg-red-50 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-red-600" />
                <Label className="text-base font-semibold text-red-800">Coordonnées GPS (validation photos)</Label>
              </div>
              <p className="text-xs text-red-600">
                Requis pour la validation GPS des photos. Les FDM avec l'option "Exiger GPS" devront prendre les photos à moins de 100m de ces coordonnées.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Latitude</Label>
                  <Input 
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    placeholder="48.8566"
                    data-testid="property-latitude"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Longitude</Label>
                  <Input 
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    placeholder="-2.7536"
                    data-testid="property-longitude"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                💡 Astuce : Recherchez l'adresse sur Google Maps, clic droit → "Plus d'infos sur cet endroit" pour obtenir les coordonnées.
              </p>
            </div>

            {/* Ring Intercom */}
            {ringIntercoms.length > 0 && (
              <div className="p-4 bg-purple-50 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-purple-600" />
                  <Label className="text-base font-semibold text-purple-800">Interphone Ring (optionnel)</Label>
                </div>
                <p className="text-xs text-purple-600">Associez un interphone Ring pour permettre aux FDM d'ouvrir la porte à distance</p>
                <Select
                  value={formData.ring_device_id || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setFormData({ ...formData, ring_device_id: '', ring_device_name: '' });
                    } else {
                      const intercom = ringIntercoms.find(i => i.id === value);
                      setFormData({ 
                        ...formData, 
                        ring_device_id: value, 
                        ring_device_name: intercom?.name || '' 
                      });
                    }
                  }}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Sélectionner un interphone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun interphone</SelectItem>
                    {ringIntercoms.map(intercom => (
                      <SelectItem key={intercom.id} value={intercom.id}>
                        {intercom.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Section Instructions d'accès */}
            <div className="p-4 bg-amber-50 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Key className="h-5 w-5 text-amber-600" />
                <Label className="text-base font-semibold text-amber-800">Instructions d'accès (optionnel)</Label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 relative">
                  <Label className="text-sm text-amber-700">Adresse boîte à clés (si différente)</Label>
                  <div className="relative">
                    <Input 
                      value={keyboxAddressQuery}
                      onChange={(e) => {
                        setKeyboxAddressQuery(e.target.value);
                        setFormData({ ...formData, keybox_address: e.target.value });
                      }}
                      onFocus={() => keyboxAddressSuggestions.length > 0 && setShowKeyboxAddressSuggestions(true)}
                      placeholder="Ex: 15 rue de la Plage"
                      className="bg-white"
                    />
                    {loadingKeyboxAddress && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-amber-500" />
                    )}
                  </div>
                  {showKeyboxAddressSuggestions && keyboxAddressSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-amber-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {keyboxAddressSuggestions.map((suggestion, index) => (
                        <button
                          key={`${suggestion.address}-${suggestion.postcode || ''}-${index}`}
                          type="button"
                          onClick={() => selectKeyboxAddress(suggestion)}
                          className="w-full px-3 py-2 text-left hover:bg-amber-50 text-sm border-b border-amber-100 last:border-0"
                        >
                          <div className="font-medium text-slate-700">{suggestion.address}</div>
                          <div className="text-xs text-slate-500">{suggestion.postcode} {suggestion.city}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-amber-700">N° boîte sur façade</Label>
                  <Input 
                    value={formData.keybox_position}
                    onChange={(e) => setFormData({ ...formData, keybox_position: e.target.value })}
                    placeholder="Ex: 2ème en partant de la gauche"
                    className="bg-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-amber-700">Instructions supplémentaires</Label>
                <Input 
                  value={formData.access_instructions}
                  onChange={(e) => setFormData({ ...formData, access_instructions: e.target.value })}
                  placeholder="Ex: Sonner au gardien, clé sous le pot de fleurs..."
                  className="bg-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Prix du ménage par défaut (€)</Label>
              <Input 
                type="number"
                value={formData.cleaning_price}
                onChange={(e) => setFormData({ ...formData, cleaning_price: e.target.value })}
                placeholder="50"
                data-testid="property-price"
              />
              <p className="text-xs text-gray-500">Ce prix sera utilisé si aucun tarif personnalisé n'est défini</p>
            </div>

            {/* Tarifs personnalisés par femme de ménage */}
            <div className="p-4 bg-emerald-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-emerald-600" />
                  <Label className="text-base font-semibold text-emerald-800">Tarifs personnalisés</Label>
                </div>
                <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                  {formData.custom_prices?.length || 0} tarif(s)
                </Badge>
              </div>
              <p className="text-sm text-emerald-600">
                Définissez des tarifs différents selon la personne assignée
              </p>

              {/* Liste des tarifs personnalisés */}
              {formData.custom_prices?.length > 0 && (
                <div className="space-y-2">
                  {formData.custom_prices.map((cp) => (
                    <div key={cp.user_id} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-emerald-200">
                      <div className="flex-1">
                        <p className="font-medium text-sm text-gray-800">{getHousekeeperName(cp.user_id)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={cp.price}
                          onChange={(e) => updateCustomPrice(cp.user_id, e.target.value)}
                          className="w-20 h-8 text-center"
                        />
                        <span className="text-sm text-gray-500">€</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCustomPrice(cp.user_id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Ajouter un tarif personnalisé */}
              {housekeepers.filter(hk => !formData.custom_prices?.find(cp => cp.user_id === hk.id)).length > 0 && (
                <Select onValueChange={(userId) => addCustomPrice(userId)}>
                  <SelectTrigger className="bg-white border-dashed border-emerald-300">
                    <div className="flex items-center gap-2 text-emerald-600">
                      <UserPlus className="h-4 w-4" />
                      <span>Ajouter un tarif personnalisé...</span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {housekeepers
                      .filter(hk => !formData.custom_prices?.find(cp => cp.user_id === hk.id))
                      .map(hk => (
                        <SelectItem key={hk.id} value={hk.id}>
                          {hk.name}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Linge de lit option */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <Label className="text-base font-semibold">Linge de lit</Label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, linen_included: true })}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    formData.linen_included 
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="linen-included-yes"
                >
                  <div className="font-medium">Inclus</div>
                  <div className="text-xs opacity-70">Linge fourni par le propriétaire</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, linen_included: false })}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    !formData.linen_included 
                      ? 'border-amber-500 bg-amber-50 text-amber-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="linen-included-no"
                >
                  <div className="font-medium">Non inclus / Option</div>
                  <div className="text-xs opacity-70">À apporter si demandé</div>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes permanentes</Label>
              <Input 
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Informations complémentaires..."
                data-testid="property-notes"
              />
            </div>

            {/* Photos d'instructions */}
            <div className="p-4 bg-blue-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold text-blue-800">Photos d'instructions</Label>
                </div>
                <Badge variant="outline" className="text-blue-600 border-blue-300">
                  {formData.instruction_photos?.length || 0} photo(s)
                </Badge>
              </div>
              <p className="text-sm text-blue-600">
                Ajoutez des photos pour guider les femmes de ménage (emplacement des produits, consignes spécifiques...)
              </p>
              
              {/* Photo Grid */}
              {formData.instruction_photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {formData.instruction_photos.map((photo) => (
                    <div key={photo} className="relative group">
                      <img 
                        src={`${API_URL}/api/photo?path=${encodeURIComponent(photo)}&auth=${localStorage.getItem('token')}`}
                        alt="Instruction"
                        className="w-full h-20 object-cover rounded-lg border border-blue-200"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(formData.instruction_photos.indexOf(photo))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                  id="instruction-photos-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-100"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhotos}
                >
                  {uploadingPhotos ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Ajouter des photos
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button className="bg-[#003366] hover:bg-[#002244]" onClick={handleSubmit} disabled={actionLoading} data-testid="save-property-btn">
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingProperty ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertiesManagement;
